import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import store from "@/store";

import { base64ToFile } from "@/common/utils/files";
import { toast } from "@/common/utils/toast";

import {
  laboratoryList,
  laboratoryCreate,
  laboratoryDelete,
  laboratoryUpload,
  laboratoryModelCSV,
} from "@/common/api/rest/laboratory";

import type {
  LaboratoryListType,
  LaboratoryPayloadType,
} from "@/common/types/laboratory";
import type { FileType } from "@/common/types/common";

import { RegisterTheme } from "@/common/themes";
import { laboratoryForm, fileUploadForm } from "@/components";
import {
  baseAvatar,
  baseConfirm,
  baseModal,
  baseTable,
  svgIcon,
} from "@/common/components";


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const id = computed(() => route.params.id as string);

const tableHeader = computed(() => [
  { key: "logo", label: null },
  { key: "name", label: t("common.labels.name") },
  { key: "document_name", label: t("common.labels.document") },
  { key: "document_number", label: t("common.labels.number") },
]);

const tableData = ref<LaboratoryListType[]>([]);

const selected = ref<LaboratoryListType | null>(null);
const payload = ref<LaboratoryPayloadType | null>(null);

const fileUpload = ref<FileType | null>(null);
const fileExtensions = ref(["csv"]);

const limit = ref<number>(5);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const isEdit = ref(false);
const showModal = ref(false);
const showConfirmDel = ref(false);
const showModalUpload = ref(false);

function fetchData() {
  laboratoryList(id.value, {
    limit: limit.value,
    page: page.value,
    search: search.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        tableData.value = data.list;
        page.value = data.infos.page;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function add() {
  showModal.value = true;
  payload.value = null;
}

function addConfirm() {
  if (!payload.value) return;

  store.commit("SET_LOADING", true);

  laboratoryCreate(id.value, payload.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        toast({ message: message, type: "success" });
        router.push(
          `/${id.value}/register/laboratory/edit/${data.laboratory_id}`,
        );
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function edit(data: LaboratoryListType) {
  router.push(`/${id.value}/register/laboratory/edit/${data.laboratory_id}`);
}

function remove(data: LaboratoryListType) {
  selected.value = data;
  showConfirmDel.value = true;
}

function removeCancel() {
  selected.value = null;
  showConfirmDel.value = false;
}

function removeConfirm() {
  if (!selected.value) return;

  store.commit("SET_LOADING", true);
  showConfirmDel.value = false;

  laboratoryDelete(id.value, selected.value.laboratory_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        removeCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function closeModal() {
  payload.value = null;
  showModal.value = false;
  isEdit.value = false;
}

function searchItem(value: string) {
  search.value = value;
  fetchData();
}

function changePage(value: number) {
  page.value = value;
  fetchData();
}

function uploadCsv() {
  showModalUpload.value = true;
}

function uploadCancel() {
  showModalUpload.value = false;
  fileUpload.value = null;
}

function uploadConfirm() {
  if (!fileUpload.value) return;

  store.commit("SET_LOADING", true);
  showModalUpload.value = false;

  laboratoryUpload(id.value, fileUpload.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        uploadCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function downloadModel() {
  laboratoryModelCSV(id.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        base64ToFile(data.file, data.name, data.type);
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

watchEffect(() => {
  if (id.value) fetchData();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(RegisterTheme), { "tab-active": "laboratory" }, {
      title: _withCtx(() => [
        _createVNode(_unref(svgIcon), {
          icon: "laboratory",
          size: "2rem"
        }),
        _createTextVNode(" " + _toDisplayString(_unref(t)("common.labels.laboratories")), 1)
      ]),
      content: _withCtx(() => [
        _createVNode(_unref(baseTable), {
          header: tableHeader.value,
          data: tableData.value,
          limit: limit.value,
          page: page.value,
          pages: pages.value,
          total: total.value,
          searched: searched.value,
          options: true,
          edit: true,
          del: true,
          upload: true,
          "onChange:page": changePage,
          onSearching: searchItem,
          "onBtn:add": _cache[0] || (_cache[0] = ($event: any) => (add())),
          "onBtn:edit": edit,
          "onBtn:del": remove,
          "onBtn:upload": _cache[1] || (_cache[1] = ($event: any) => (uploadCsv()))
        }, {
          logo: _withCtx(({ data }) => [
            _createVNode(_unref(baseAvatar), {
              url: data.avatar?.small,
              size: "1.75rem",
              background: "#ffffff"
            }, null, 8, ["url"])
          ]),
          _: 1
        }, 8, ["header", "data", "limit", "page", "pages", "total", "searched"])
      ]),
      _: 1
    }),
    _createVNode(_unref(baseConfirm), {
      show: showConfirmDel.value,
      title: 
      _unref(t)('common.labels.remove', {
        concat: _unref(t)('common.labels.laboratory'),
      }).toUpperCase()
    ,
      message: 
      _unref(t)('common.texts.remove_message', {
        concat: selected.value?.name,
      })
    ,
      onCancel: removeCancel,
      onConfirm: removeConfirm
    }, null, 8, ["show", "title", "message"]),
    _createVNode(_unref(baseModal), {
      show: showModal.value,
      title: 
      _unref(t)('common.labels.add', {
        concat: _unref(t)('common.labels.laboratory'),
      }).toUpperCase()
    ,
      "show-button-confirm": true,
      "button-confirm": _unref(t)('common.labels.add'),
      "button-close": _unref(t)('common.labels.cancel'),
      onClose: _cache[3] || (_cache[3] = ($event: any) => (closeModal())),
      onConfirm: _cache[4] || (_cache[4] = ($event: any) => (addConfirm()))
    }, {
      content: _withCtx(() => [
        _createVNode(_unref(laboratoryForm), {
          data: payload.value,
          onUpdateData: _cache[2] || (_cache[2] = ($event: any) => (payload.value = $event))
        }, null, 8, ["data"])
      ]),
      _: 1
    }, 8, ["show", "title", "button-confirm", "button-close"]),
    _createVNode(_unref(baseModal), {
      show: showModalUpload.value,
      title: 
      _unref(t)('common.labels.upload', {
        concat: _unref(t)('common.labels.company'),
      }).toUpperCase()
    ,
      "show-button-confirm": true,
      "button-confirm": _unref(t)('common.labels.send'),
      "button-close": _unref(t)('common.labels.cancel'),
      onClose: _cache[7] || (_cache[7] = ($event: any) => (uploadCancel())),
      onConfirm: _cache[8] || (_cache[8] = ($event: any) => (uploadConfirm()))
    }, {
      content: _withCtx(() => [
        _createVNode(_unref(fileUploadForm), {
          extensions: fileExtensions.value,
          model: true,
          onUpdateData: _cache[5] || (_cache[5] = ($event: any) => (fileUpload.value = $event)),
          onModelDownload: _cache[6] || (_cache[6] = ($event: any) => (downloadModel()))
        }, null, 8, ["extensions"])
      ]),
      _: 1
    }, 8, ["show", "title", "button-confirm", "button-close"])
  ], 64))
}
}

})